//Navbar.tsx
"use client";
import * as React from "react";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Image from "next/image";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import LanguageSwitcher from "@/app/components/LanguageSwitcher";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import { Divider, Typography } from "@mui/material";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "50px",
  backgroundColor: "#f8f6ef",
  "&:hover": {
    backgroundColor: "#f8f6ef",
  },
  marginLeft: 0,
  width: "auto",
  minWidth: "34px",
  [theme.breakpoints.up("lg")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "34px",
  width: "34px",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 3,
  cursor: "pointer",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  width: "100%",
  "& .MuiInputBase-input": {
    padding: "6px 8px 5px 0",
    paddingLeft: `calc(${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    color: theme.palette.$TCPG500,
    [theme.breakpoints.up("lg")]: {
      width: "0",
      padding: "6px 8px 5px 0",
      paddingLeft: `calc(${theme.spacing(3)})`,
    },
    "&:focus, &:hover": {
      paddingLeft: `calc(0.5em + ${theme.spacing(3)})`,
      width: "10ch",
    },
  },
}));

export default function AppAppBar() {
  const t = useTranslations("Menu");
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const productsMenuOpen = Boolean(anchorEl);
  const [mobileProductsOpen, setMobileProductsOpen] = React.useState(false);
  const tProducts = useTranslations("OurProducts");

  const [servicesAnchorEl, setServicesAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const servicesMenuOpen = Boolean(servicesAnchorEl);
  const [mobileServicesOpen, setMobileServicesOpen] = React.useState(false);

  const [hash, setHash] = React.useState("");


  const SUPPORTED_LOCALES = ["en", "zh"] as const;
  type Locale = (typeof SUPPORTED_LOCALES)[number];

  const pathname = usePathname() || "/";
  const firstSeg = pathname.split("/")[1] || "";
  const locale: Locale = (SUPPORTED_LOCALES as readonly string[]).includes(
    firstSeg
  )
    ? (firstSeg as Locale)
    : "en";

  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries)
          if (entry.isIntersecting) setHash(`#${entry.target.id}`);
      },
      { rootMargin: "-40% 0px -60% 0px", threshold: 0 }
    );

    const sections: Element[] = [];
    productLinks.forEach((link) => {
      const id = link.href.split("#")[1];
      if (id) {
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          sections.push(el);
        }
      }
    });

    const onHashChange = () => setHash(window.location.hash);
    setHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", onHashChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, locale]); // re-run เมื่อ locale เปลี่ยน

  // ใหม่:
  const href = (path: string) => {
    const seg = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
    if (locale === "en") return seg || "/"; // ✅ EN → ไม่มี /en
    return `/${locale}${seg}`; // CH → /ch/...
  };

  // ใช้ helper นี้สร้างเมนู
  const navLinks = [
    { label: t("Home"), href: href("/") },
    { label: t("Products"), href: href("/thailand-premium-tapioca-starch-products") },
    { label: t("Services"), href: href("/thailand-tapioca-starch-manufacturing-export-services-fcl-lcl") },
    { label: t("News"), href: href("/news") },
    { label: t("FAQ"), href: href("/faq") },
    { label: t("Contact"), href: href("/contact-us") },
  ];

  // ใหม่:
  const productLinks = [
    {
      label: tProducts("Hero.menu"),
      href: `${href("/thailand-premium-tapioca-starch-products")}#tapioca-starch`,
    },
    {
      label: tProducts("FoodGrade.menu"),
      href: `${href("/thailand-premium-tapioca-starch-products")}#food-grade`,
    },
    {
      label: tProducts("IndustrialGrade.menu"),
      href: `${href("/thailand-premium-tapioca-starch-products")}#industrial-grade`,
    },
    {
      label: tProducts("Pearl.title"),
      href: `${href("/thailand-premium-tapioca-starch-products")}#tapioca-pearl`,
    },
    {
      label: tProducts("Chips.title"),
      href: `${href("/thailand-premium-tapioca-starch-products")}#tapioca-chips`,
    },
  ];

  // Submenu ใต้ "Our Services" — ตอนนี้มีแค่ Manufacturing Process
  const servicesLinks = [
    {
      label: t("ManufacturingProcess"),
      href: href("/native-tapioca-starch-manufacturing-process"),
    },
  ];

  const handleProductsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleServicesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (servicesAnchorEl !== event.currentTarget) {
      setServicesAnchorEl(event.currentTarget);
    }
  };

  const handleServicesMenuClose = () => {
    setServicesAnchorEl(null);
  };

  const handleProductsMenuClose = () => {
    setAnchorEl(null);
  };

  const isActive = (url: string) => {
    const [pathOnly, hrefHash] = url.split("#");

    // โฮมเพจ: EN ให้ถือว่า active เมื่อ pathname เป็น '/' หรือ '/en' (กรณี SSR)
    if (pathOnly === href("/")) {
      if (locale === "en") return pathname === "/" || pathname === "/en";
      return pathname === `/${locale}`;
    }

    // ถ้าเป็นลิงก์มี hash: ยังไม่ mounted → อย่าใช้ hash ตัดสิน (กัน SSR/CSR ต่าง)
    if (hrefHash) {
      if (!mounted) return pathname === pathOnly;
      return pathname === pathOnly && hash === `#${hrefHash}`;
    }

    // หน้าอื่น ๆ: ตรง path ก็ active
    return pathname ? pathname.startsWith(pathOnly) : false;
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <AppBar
      className="navbar"
      sx={{
        boxShadow: 0,
        py: 2,
      }}
    >
      <Container className="nav-container">
        <Toolbar variant="dense" disableGutters>
          <Box
            sx={{ flexGrow: 1, display: "flex", alignItems: "center", px: 0 }}
          >
            <Link
              href="https://www.poonphol.com/en/home"
              style={{ display: "flex", alignItems: "center" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src="/assets/logo.svg"
                title="Tapioca Logo"
                alt="Tapioca Logo"
                width={128}
                height={50}
              />
            </Link>
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                justifyContent: "flex-end",
                flexGrow: 1,
                alignItems: "center",
                gap: 2,
              }}
            >
              {navLinks.map((link) => {
                if (link.label === t("Products")) {
                  return (
                    <Box
                      key={link.label}
                      onMouseLeave={handleProductsMenuClose}
                      sx={{ position: "relative" }}
                    >
                      <Button
                        aria-owns={
                          productsMenuOpen ? "products-menu" : undefined
                        }
                        aria-haspopup="true"
                        onMouseEnter={handleProductsMenuOpen}
                        variant="text"
                        disableRipple={true}
                        disableElevation={true}
                        disableFocusRipple={true}
                        href={link.href}
                        className={
                          isActive(link.href)
                            ? "navbar-button active"
                            : "navbar-button"
                        }
                        sx={{
                          color: isActive(link.href)
                            ? (theme) => theme.palette.$TCPYL500
                            : (theme) => theme.palette.$TCPG500,
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: (theme) => theme.palette.$TCPYL500,
                          },
                        }}
                      >
                        {link.label}
                        
                      </Button>
                      {/*<Menu
                        id="products-menu"
                        anchorEl={anchorEl}
                        open={productsMenuOpen}
                        onClose={handleProductsMenuClose}
                        MenuListProps={{
                          onMouseLeave: handleProductsMenuClose,
                        }}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        <Link
                          key={link.href}
                          href={link.href}
                          passHref
                          style={{ textDecoration: "none", color: "inherit" }}
                          className="main-link"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = link.href;
                          }}
                        >
                          {link.label}
                        </Link>
                        <Box className="products-menu-box">
                          <Typography variant="body1" mb={1}>
                            {tProducts("export")}
                          </Typography>

                          {productLinks.map((productLink) => (
                            <Link
                              key={productLink.href}
                              href={productLink.href}
                              passHref
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              className={
                                isActive(productLink.href) ? "active" : ""
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                window.location.href = productLink.href;
                              }}
                            >
                              {productLink.label}
                            </Link>
                          ))}
                          <Divider sx={{ mt: 1, mb: 2 }} />
                          <Typography variant="body1" mb={1}>
                            {tProducts("import.menu")}
                          </Typography>
                          <Link
                            href={`${href("/thailand-premium-tapioca-starch-products")}#contact`}
                            passHref
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={(e) => {
                              e.preventDefault();
                              window.location.href = `${href(
                                "/thailand-premium-tapioca-starch-products"
                              )}#contact`;
                            }}
                          >
                            {tProducts("import.CornStarch")}
                          </Link>
                          <Link
                            href={`${href("/thailand-premium-tapioca-starch-products")}#contact`}
                            passHref
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={(e) => {
                              e.preventDefault();
                              window.location.href = `${href(
                                "/thailand-premium-tapioca-starch-products"
                              )}#contact`;
                            }}
                          >
                            {tProducts("import.Maltodextrin")}
                          </Link>
                        </Box>
                      </Menu>*/}
                      <Box
                        id="products-menu"
                        sx={{
                          height: productsMenuOpen ? "auto" : 0,
                          overflow: "hidden",
                          position: "absolute",
                          top: locale === "en" ? "37px" : "31px",
                        }}
                      >
                        <Box className="products-menu-box">
                          <Typography variant="body1" sx={{
                            mb: 1
                          }}>
                            {tProducts("export")}
                          </Typography>

                          {productLinks.map((productLink) => (
                            <Link
                              key={productLink.href}
                              href={productLink.href}
                              passHref
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              className={
                                isActive(productLink.href) ? "active" : ""
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                window.location.href = productLink.href;
                              }}
                            >
                              {productLink.label}
                            </Link>
                          ))}
                          <Divider sx={{ mt: 1, mb: 2 }} />
                          <Typography variant="body1" sx={{
                            mb: 1
                          }}>
                            {tProducts("import.menu")}
                          </Typography>
                          <Link
                            href={`${href("/thailand-premium-tapioca-starch-products")}#contact`}
                            passHref
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={(e) => {
                              e.preventDefault();
                              window.location.href = `${href(
                                "/thailand-premium-tapioca-starch-products"
                              )}#contact`;
                            }}
                          >
                            {tProducts("import.CornStarch")}
                          </Link>
                          <Link
                            href={`${href("/thailand-premium-tapioca-starch-products")}#contact`}
                            passHref
                            style={{ textDecoration: "none", color: "inherit" }}
                            onClick={(e) => {
                              e.preventDefault();
                              window.location.href = `${href(
                                "/thailand-premium-tapioca-starch-products"
                              )}#contact`;
                            }}
                          >
                            {tProducts("import.Maltodextrin")}
                          </Link>
                        </Box>
                      </Box>
                    </Box>
                  );
                }
                if (link.label === t("Services")) {
                  return (
                    <Box
                      key={link.label}
                      onMouseLeave={handleServicesMenuClose}
                      sx={{ position: "relative" }}
                    >
                      <Button
                        aria-owns={
                          servicesMenuOpen ? "services-menu" : undefined
                        }
                        aria-haspopup="true"
                        onMouseEnter={handleServicesMenuOpen}
                        variant="text"
                        disableRipple={true}
                        disableElevation={true}
                        disableFocusRipple={true}
                        href={link.href}
                        className={
                          isActive(link.href)
                            ? "navbar-button active"
                            : "navbar-button"
                        }
                        sx={{
                          color: isActive(link.href)
                            ? (theme) => theme.palette.$TCPYL500
                            : (theme) => theme.palette.$TCPG500,
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: (theme) => theme.palette.$TCPYL500,
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                      <Box
                        id="services-menu"
                        sx={{
                          height: servicesMenuOpen ? "auto" : 0,
                          overflow: "hidden",
                          position: "absolute",
                          top: locale === "en" ? "37px" : "31px",
                        }}
                      >
                        <Box className="products-menu-box">
                          {servicesLinks.map((serviceLink) => (
                            <Link
                              key={serviceLink.href}
                              href={serviceLink.href}
                              passHref
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              className={
                                isActive(serviceLink.href) ? "active" : ""
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                window.location.href = serviceLink.href;
                              }}
                            >
                              {serviceLink.label}
                            </Link>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Link key={link.href} href={link.href} passHref>
                    <Button
                      variant="text"
                      disableRipple={true}
                      disableElevation={true}
                      disableFocusRipple={true}
                      className={
                        isActive(link.href)
                          ? "navbar-button active"
                          : "navbar-button"
                      }
                      sx={{
                        color: isActive(link.href)
                          ? (theme) => theme.palette.$TCPYL500
                          : (theme) => theme.palette.$TCPG500,
                        "&:hover": {
                          backgroundColor: "transparent",
                          color: (theme) => theme.palette.$TCPYL500,
                        },
                      }}
                    >
                      {link.label}
                    </Button>
                  </Link>
                );
              })}
              <LanguageSwitcher />
            </Box>
            <Box
              className="menu-search"
              sx={{
                display: { xs: "none", lg: "block" },
              }}
            >
              {/*<Search>
                <SearchIconWrapper>
                  <SearchIcon className="search-icon" />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder={t("Search")}
                  inputProps={{ "aria-label": "input-menu-search" }}
                  id="input-menu-search"
                />
              </Search>*/}
            </Box>
          </Box>
          <Box sx={{ display: { xs: "flex", lg: "none" }, gap: 1 }}>
            <LanguageSwitcher />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              className="menu-drawer"
              anchor="right"
              open={open}
              onClose={toggleDrawer(false)}
              slotProps={{
                paper: {
                  sx: {
                    top: "var(--template-frame-height, 0px)",
                    width: "80%",
                  },
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon
                      sx={{ color: (theme) => theme.palette.common.white }}
                    />
                  </IconButton>
                </Box>

                <List sx={{ p: 0 }}>
                  {navLinks.map((link) => {
                    if (link.label === t("Products")) {
                      return (
                        <React.Fragment key={link.label}>
                          <ListItemButton
                            onClick={() =>
                              setMobileProductsOpen(!mobileProductsOpen)
                            }
                            sx={{ py: 1.5, px: 0 }}
                          >
                            <ListItemText
                              primary={link.label}
                              className={isActive(link.href) ? "active" : ""}
                              sx={{
                                color: isActive(link.href)
                                  ? (theme) => theme.palette.$TCPYL500
                                  : "white",
                              }}
                            />
                            {mobileProductsOpen ? (
                              <ArrowDropUpIcon
                                sx={{
                                  color: isActive(link.href)
                                    ? (theme) => theme.palette.$TCPYL500
                                    : "white",
                                }}
                              />
                            ) : (
                              <ArrowDropDownIcon
                                sx={{
                                  color: isActive(link.href)
                                    ? (theme) => theme.palette.$TCPYL500
                                    : "white",
                                }}
                              />
                            )}
                          </ListItemButton>
                          <Collapse
                            in={mobileProductsOpen}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List
                              component="div"
                              disablePadding
                              className="mobile-products-list"
                            >
                              <Typography variant="body1" sx={{
                                mb: 1
                              }}>
                                {tProducts("export")}
                              </Typography>
                              {productLinks.map((productLink) => (
                                <ListItem
                                  key={productLink.href}
                                  sx={{ py: 0, pl: 0 }}
                                >
                                  <Link
                                    href={productLink.href}
                                    passHref
                                    onClick={toggleDrawer(false)}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                      width: "100%",
                                    }}
                                  >
                                    <ListItemButton sx={{ py: 0, px: 0 }}>
                                      <ListItemText
                                        primary={productLink.label}
                                        className={
                                          isActive(productLink.href)
                                            ? "active"
                                            : ""
                                        }
                                        sx={{
                                          color: isActive(productLink.href)
                                            ? (theme) => theme.palette.$TCPYL500
                                            : undefined,
                                        }}
                                      />
                                    </ListItemButton>
                                  </Link>
                                </ListItem>
                              ))}
                              <Divider sx={{ mt: 1, mb: 2 }} />
                              <Typography variant="body1" sx={{
                                mb: 1
                              }}>
                                {tProducts("import.menu")}
                              </Typography>
                              <ListItem sx={{ py: 0, pl: 0 }}>
                                <Link
                                  href={`${href("/thailand-premium-tapioca-starch-products")}#contact`}
                                  passHref
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                  }}
                                >
                                  <ListItemButton sx={{ py: 0, px: 0 }}>
                                    <ListItemText>
                                      {tProducts("import.CornStarch")}
                                    </ListItemText>
                                  </ListItemButton>
                                </Link>
                              </ListItem>
                              <ListItem sx={{ py: 0, pl: 0 }}>
                                <Link
                                  href={`${href("/thailand-premium-tapioca-starch-products")}#contact`}
                                  passHref
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                  }}
                                >
                                  <ListItemButton sx={{ py: 0, px: 0 }}>
                                    <ListItemText>
                                      {tProducts("import.Maltodextrin")}
                                    </ListItemText>
                                  </ListItemButton>
                                </Link>
                              </ListItem>
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                    }
                    if (link.label === t("Services")) {
                      return (
                        <React.Fragment key={link.label}>
                          <ListItemButton
                            onClick={() =>
                              setMobileServicesOpen(!mobileServicesOpen)
                            }
                            sx={{ py: 1.5, px: 0 }}
                          >
                            <ListItemText
                              primary={link.label}
                              className={isActive(link.href) ? "active" : ""}
                              sx={{
                                color: isActive(link.href)
                                  ? (theme) => theme.palette.$TCPYL500
                                  : "white",
                              }}
                            />
                            {mobileServicesOpen ? (
                              <ArrowDropUpIcon
                                sx={{
                                  color: isActive(link.href)
                                    ? (theme) => theme.palette.$TCPYL500
                                    : "white",
                                }}
                              />
                            ) : (
                              <ArrowDropDownIcon
                                sx={{
                                  color: isActive(link.href)
                                    ? (theme) => theme.palette.$TCPYL500
                                    : "white",
                                }}
                              />
                            )}
                          </ListItemButton>
                          <Collapse
                            in={mobileServicesOpen}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List
                              component="div"
                              disablePadding
                              className="mobile-products-list"
                            >
                              {servicesLinks.map((serviceLink) => (
                                <ListItem
                                  key={serviceLink.href}
                                  sx={{ py: 0, pl: 0 }}
                                >
                                  <Link
                                    href={serviceLink.href}
                                    passHref
                                    onClick={toggleDrawer(false)}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                      width: "100%",
                                    }}
                                  >
                                    <ListItemButton sx={{ py: 0, px: 0 }}>
                                      <ListItemText
                                        primary={serviceLink.label}
                                        className={
                                          isActive(serviceLink.href)
                                            ? "active"
                                            : ""
                                        }
                                        sx={{
                                          color: isActive(serviceLink.href)
                                            ? (theme) => theme.palette.$TCPYL500
                                            : undefined,
                                        }}
                                      />
                                    </ListItemButton>
                                  </Link>
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                    }
                    return (
                      <ListItem key={link.href} sx={{ p: 0 }}>
                        <Link
                          href={link.href}
                          passHref
                          onClick={toggleDrawer(false)}
                          style={{
                            textDecoration: "none",
                            color: "inherit",
                            width: "100%",
                          }}
                        >
                          <ListItemButton sx={{ py: 1.5, px: 0 }}>
                            <ListItemText
                              primary={link.label}
                              className={isActive(link.href) ? "active" : ""}
                              sx={{
                                color: isActive(link.href)
                                  ? (theme) => theme.palette.$TCPYL500
                                  : undefined,
                              }}
                            />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    );
                  })}
                </List>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    mt: 2
                  }}>
                  <LanguageSwitcher />
                  {/*<Box className="menu-search">
                    <Search>
                      <SearchIconWrapper>
                        <SearchIcon className="search-icon" />
                      </SearchIconWrapper>
                      <StyledInputBase
                        placeholder={t("Search")}
                        inputProps={{ "aria-label": "input-menu-search" }}
                        id="input-menu-search"
                      />
                    </Search>
                  </Box>*/}
                </Box>
              </Box>
            </Drawer>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
